import { Field, Formik } from 'formik';
import React from 'react';
import { Banner, Button, Division, Input, ModalBox, Select, useModalContext, } from '@fourthwall/components';
import { getPrice } from '@fourthwall/utils';
import { isRequired } from '@fourthwall/utils/dist/validation';
import { PaymentMethodRequiredModal } from '@modules/Dashboard/modules/PaymentMethods';
import { useCalculateCancelRefundsQuery } from '../../queries/useCalculateCancelRefundsQuery';
import * as S from './OrderCancelConfirmModal.styled';
export const OrderCancelConfirmModal = ({ orderId, itemsToCancel, content, reasonOptions, initialReason, error, onConfirm, }) => {
    const { open, close } = useModalContext();
    const { calculateCancelRefundsQuery } = useCalculateCancelRefundsQuery([
        orderId,
        { itemsToCancel },
    ]);
    const initialValues = {
        reason: initialReason || '',
    };
    const openPaymentMethodRequiredModal = (values) => {
        if (calculateCancelRefundsQuery.data?.estimatedCost.type !== 'LOSS' ||
            calculateCancelRefundsQuery.data?.estimatedCost.chargeDetails.type !== 'REQUIRED') {
            return;
        }
        const chargeDetails = calculateCancelRefundsQuery.data?.estimatedCost.chargeDetails;
        const fromBalance = calculateCancelRefundsQuery.data?.estimatedCost.fromBalance;
        const balancePrice = getPrice(fromBalance.value);
        const remainingPrice = getPrice(chargeDetails.amount.value);
        const balanceSplitCopy = fromBalance.value > 0
            ? `${balancePrice} will be deducted from your account balance and the remaining ${remainingPrice} will be charged to your payment method. `
            : '';
        open(PaymentMethodRequiredModal, {
            title: `Are you sure you want to cancel these items?`,
            subtitle: (React.createElement(React.Fragment, null,
                React.createElement("p", null,
                    balanceSplitCopy,
                    "Refunds are sent directly to the supporter's original payment method."),
                React.createElement("p", null,
                    React.createElement("b", null,
                        "Payment method to cover ",
                        remainingPrice)))),
            submitLabel: `Confirm`,
            onSuccess: () => {
                onConfirm({
                    fromBalance,
                    charge: chargeDetails.amount,
                }, values.reason.length ? values.reason : undefined);
            },
        });
    };
    const handleSubmit = (values) => {
        close();
        const estimatedCost = calculateCancelRefundsQuery.data?.estimatedCost;
        const zeroUSD = { value: 0, currency: 'USD' };
        const reason = values.reason.length ? values.reason : undefined;
        if (!estimatedCost) {
            // That case should never happen
            onConfirm(undefined, reason);
            return;
        }
        if (estimatedCost.type === 'NO_LOSS') {
            onConfirm({ fromBalance: zeroUSD, charge: zeroUSD }, reason);
            return;
        }
        if (estimatedCost.type === 'LOSS') {
            const { fromBalance, chargeDetails } = estimatedCost;
            if (chargeDetails.type === 'NOT_REQUIRED') {
                onConfirm({ fromBalance, charge: zeroUSD }, reason);
                return;
            }
            if (chargeDetails.type === 'REQUIRED') {
                openPaymentMethodRequiredModal(values);
            }
        }
    };
    const renderInput = () => {
        if (reasonOptions) {
            return (React.createElement(Field, { component: Select, name: "reason", label: "Refund reason", options: reasonOptions, validate: isRequired('Please select a refund reason. This reason will appear in TikTok Seller Center.'), suggestion: "This reason will appear in TikTok Seller Center." }));
        }
        return (React.createElement(Field, { component: Input, name: "reason", label: "Refund reason (optional)", suggestion: "Only you and other managers can see this reason." }));
    };
    return (React.createElement(Formik, { initialValues: initialValues, enableReinitialize: true, onSubmit: handleSubmit }, ({ submitForm }) => (React.createElement(ModalBox, { title: "Are you sure you want to cancel these items?", buttons: [
            React.createElement(Button, { label: "Back", appearance: "secondary", onClick: close }),
            React.createElement(Button, { label: "Yes, cancel these items", appearance: "destructive", loading: calculateCancelRefundsQuery.isFetching, onClick: submitForm }),
        ], onClose: close },
        error && (React.createElement(S.ErrorBannerContainer, null,
            React.createElement(Banner, { appearance: "danger" }, error))),
        content,
        React.createElement(Division, { size: "small" }, "Once the items are canceled, this cannot be undone. The cost of refunding these items will be taken from your account. Refunds will be sent directly to the supporter's payment method (it may take up to 48 hours to process) and the items will not be delivered to them."),
        React.createElement(S.InputContainer, null, renderInput())))));
};
